import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { User, Package, Heart, MapPin, Crown } from "lucide-react";
import api, { money } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TABS = [
  { id: "profile", label: "Profile", icon: User },
  { id: "orders", label: "Orders", icon: Package },
  { id: "wishlist", label: "Wishlist", icon: Heart },
  { id: "addresses", label: "Addresses", icon: MapPin },
];

const statusColor = (s) => ({ confirmed: "text-[#D4AF37]", processing: "text-[#FAFAF7]/60", shipped: "text-emerald-400", delivered: "text-emerald-400", cancelled: "text-[#58111A]" }[s] || "text-[#FAFAF7]/60");

export default function Account() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "profile";
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);

  useEffect(() => {
    if (tab === "orders") api.get("/account/orders").then((r) => setOrders(r.data));
    if (tab === "wishlist") api.get("/account/wishlist").then((r) => setWishlist(r.data));
  }, [tab]);

  return (
    <div className="bg-[#0B0B0C] min-h-screen pt-28 sm:pt-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8">
        {/* Royal tier banner */}
        <div className="gold-border rounded-xl bg-gradient-to-r from-[#141416] to-[#0B0B0C] p-6 sm:p-8 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="eyebrow mb-2 flex items-center gap-2"><Crown size={14} className="text-[#D4AF37]" /> ROYAL GOLD MEMBER</p>
            <h1 className="font-serif-lux text-3xl sm:text-4xl font-light text-[#FAFAF7]">Bonjour, {user?.name}</h1>
            <p className="text-sm text-[#FAFAF7]/50 mt-1">{user?.email}</p>
          </div>
          <div className="text-right">
            <p className="font-serif-lux text-4xl gold-text">1,240</p>
            <p className="eyebrow text-[0.6rem]">ROYAL POINTS</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
          <aside className="flex lg:flex-col gap-2 overflow-x-auto">
            {TABS.map((t) => (
              <button key={t.id} data-testid={`account-tab-${t.id}`} onClick={() => setParams({ tab: t.id })}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm whitespace-nowrap transition-colors ${tab === t.id ? "bg-[#D4AF37]/10 text-[#D4AF37]" : "text-[#FAFAF7]/60 hover:bg-white/5"}`}>
                <t.icon size={16} /> {t.label}
              </button>
            ))}
          </aside>

          <div className="min-h-[300px]">
            {tab === "profile" && (
              <div className="gold-border rounded-xl bg-[#141416] p-8 space-y-4">
                <h2 className="font-serif-lux text-2xl text-[#FAFAF7] mb-4">Profile Details</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><p className="eyebrow text-[0.6rem] mb-1">Name</p><p className="text-[#FAFAF7]">{user?.name}</p></div>
                  <div><p className="eyebrow text-[0.6rem] mb-1">Email</p><p className="text-[#FAFAF7]">{user?.email}</p></div>
                  <div><p className="eyebrow text-[0.6rem] mb-1">Member Tier</p><p className="text-[#D4AF37]">Royal Gold</p></div>
                  <div><p className="eyebrow text-[0.6rem] mb-1">Sign-in Method</p><p className="text-[#FAFAF7] capitalize">{user?.auth_provider}</p></div>
                </div>
              </div>
            )}

            {tab === "orders" && (
              <div className="space-y-4">
                {orders.length === 0 ? (
                  <div className="gold-border rounded-xl bg-[#141416] p-12 text-center">
                    <Package size={36} className="text-[#D4AF37]/40 mx-auto mb-4" />
                    <p className="text-[#FAFAF7]/60 font-serif-lux text-xl mb-4">No orders yet</p>
                    <Link to="/shop" className="btn-outline-gold px-6 py-2 rounded-full text-sm inline-block">Start Shopping</Link>
                  </div>
                ) : orders.map((o) => (
                  <div key={o.id} data-testid={`order-${o.id}`} className="gold-border rounded-xl bg-[#141416] p-5">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="text-xs text-[#FAFAF7]/40">Order #{o.id.slice(-8)}</p>
                        <p className="text-xs text-[#FAFAF7]/40">{new Date(o.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <p className={`text-sm capitalize ${statusColor(o.status)}`}>{o.status}</p>
                        <p className="font-serif-lux text-xl text-[#D4AF37]">{money(o.total)}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {o.items.map((it, i) => <span key={i} className="text-xs text-[#FAFAF7]/60 bg-white/5 px-2 py-1 rounded">{it.name} ×{it.quantity}</span>)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === "wishlist" && (
              <div>
                {wishlist.length === 0 ? (
                  <div className="gold-border rounded-xl bg-[#141416] p-12 text-center">
                    <Heart size={36} className="text-[#D4AF37]/40 mx-auto mb-4" />
                    <p className="text-[#FAFAF7]/60 font-serif-lux text-xl">Your wishlist is empty</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
                    {wishlist.map((p) => (
                      <Link key={p.id} to={`/product/${p.id}`} className="group">
                        <div className="aspect-[3/4] rounded-lg overflow-hidden bg-[#141416] mb-2">
                          <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        </div>
                        <p className="font-serif-lux text-lg text-[#FAFAF7]">{p.name}</p>
                        <p className="text-sm text-[#D4AF37]">{money(p.price)}</p>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {tab === "addresses" && (
              <div className="gold-border rounded-xl bg-[#141416] p-8 text-center">
                <MapPin size={36} className="text-[#D4AF37]/40 mx-auto mb-4" />
                <p className="text-[#FAFAF7]/60 font-serif-lux text-xl mb-2">No saved addresses</p>
                <p className="text-sm text-[#FAFAF7]/40">Your shipping address is collected at checkout.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
